// ---------------------------------------------------------------------------
// How much quoted pipeline a target needs, from the salesperson's own record: of the deals they quoted that closed in
// the last 12 months, the share of the value they won (win rate by value), and the median days from quotation sent to
// the PO. With fewer than MIN_CLOSED_QUOTES closed quotations — or no win at all — the company's rate is used instead
// (flagged); without a usable company rate, nothing is worked out (never a division by zero).
//   neededYear  quotation value to send over the year: target ÷ win rate
//   neededNow   open quoted pipeline needed today: what is still to win ÷ win rate
// "Quote by" dates are estimates: each quarter's target, quoted the median time-to-PO before the quarter ends. The time
// to PO counts only wins whose quotation went out before the PO (a quotation date filled in on the day of the win says
// nothing), and needs MIN_CYCLES of them — the person's own, else the company's; else no quote-by date is given.
// ---------------------------------------------------------------------------

/** Closed quotations a person needs before their own win rate is used. */
export const MIN_CLOSED_QUOTES = 5;
/** Wins quoted before their PO needed before a median time to PO is used. */
export const MIN_CYCLES = 5;

/** The last 12 months of quoted deals that closed (won or lost). */
export interface WinHistory {
  wonValue: number; lostValue: number; won: number; closed: number;
  /** Median days from quotation sent to the PO, over the wins quoted before their PO (null with none), and how many. */
  cycleDays: number | null; cycles: number;
}

export type RateSource = 'own' | 'company' | 'none';
/** Why a person's own rate isn't used: too few closed quotations, or none of them won. */
export type RateFlag = 'thin' | 'zero' | null;

export interface PickedRate { rate: number | null; source: RateSource; flag: RateFlag; cycleDays: number | null; history: WinHistory }

/** Win rate by value (null when nothing closed, or nothing had a value). */
export function valueRate(h: WinHistory): number | null {
  const total = h.wonValue + h.lostValue;
  return total > 0 ? h.wonValue / total : null;
}

const usable = (h: WinHistory | null | undefined): boolean => !!h && h.closed >= MIN_CLOSED_QUOTES && (valueRate(h) ?? 0) > 0;

/** The rate a person's figures use: their own, else the company's (flagged why), else none. */
export function pickRate(own: WinHistory | null | undefined, company: WinHistory | null | undefined): PickedRate {
  const mine = own ?? { wonValue: 0, lostValue: 0, won: 0, closed: 0, cycleDays: null, cycles: 0 };
  // Time to PO: theirs with enough wins quoted before the PO, else the company's, else unknown.
  const cycleDays = mine.cycles >= MIN_CYCLES ? mine.cycleDays : company && company.cycles >= MIN_CYCLES ? company.cycleDays : null;
  if (usable(mine)) return { rate: valueRate(mine), source: 'own', flag: null, cycleDays, history: mine };
  const flag: RateFlag = mine.closed >= MIN_CLOSED_QUOTES ? 'zero' : 'thin';
  if (company && usable(company)) return { rate: valueRate(company), source: 'company', flag, cycleDays, history: mine };
  return { rate: null, source: 'none', flag, cycleDays, history: mine };
}

/** Quotation value needed to win `amount` at `rate` (null without a usable rate). */
export const quotedFor = (amount: number, rate: number | null) => (rate != null && rate > 0 ? Math.max(0, amount) / rate : null);

const addDays = (ymd: string, n: number) => { const d = new Date(`${ymd}T00:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
const quarterEnd = (year: number, q: number) => addDays(`${q === 4 ? year + 1 : year}-${String(q === 4 ? 1 : q * 3 + 1).padStart(2, '0')}-01`, -1);

export interface QuoteBy { quarter: number; amount: number; by: string; late: boolean }

/**
 * Per quarter of `year`: the quotation value its target needs and the (estimated) day it should be quoted by — the
 * quarter's last day minus the median time to PO. `late` when that day is already past.
 */
export function quoteByPlan(year: number, quarterTargets: number[], rate: number | null, cycleDays: number | null, today: string): QuoteBy[] {
  if (rate == null || rate <= 0 || cycleDays == null) return [];
  return quarterTargets.map((t, i) => ({ quarter: i + 1, amount: t / rate, by: addDays(quarterEnd(year, i + 1), -Math.round(cycleDays)), late: false }))
    .filter((x) => x.amount > 0)
    .map((x) => ({ ...x, late: x.by < today }));
}

export interface PipelineNeed extends PickedRate {
  target: number | null;
  /** Quotation value to send over the year, and open quoted pipeline needed today (null without a rate or target). */
  neededYear: number | null; neededNow: number | null;
  /** Open quoted pipeline (quotation sent, PO expected in the year) over what's needed today (null when unknown). */
  coverage: number | null;
  /** The target is already won (nothing more needed). */
  reached: boolean;
  /** The next quarter still to quote for (the first not late), and whether earlier ones are already late. */
  next: QuoteBy | null; lateQuarters: number;
}

/** Everything a person's (or the company's) "pipeline needed" shows. */
export function pipelineNeed(p: { target: number | null; won: number; quoted: number; quarterTargets: number[]; own: WinHistory | null; company: WinHistory | null; year: number; today: string }): PipelineNeed {
  const picked = pickRate(p.own, p.company);
  const neededYear = p.target ? quotedFor(p.target, picked.rate) : null;
  const neededNow = p.target ? quotedFor(p.target - p.won, picked.rate) : null;
  const plan = quoteByPlan(p.year, p.quarterTargets, picked.rate, picked.cycleDays, p.today);
  return {
    ...picked, target: p.target, neededYear, neededNow,
    coverage: neededNow != null && neededNow > 0 ? p.quoted / neededNow : null, reached: !!p.target && p.won >= p.target,
    next: plan.find((x) => !x.late) ?? null, lateQuarters: plan.filter((x) => x.late).length,
  };
}

/** Red below half of what's needed today, amber up to all of it, green from all of it. */
export const coverageTone = (coverage: number | null) => (coverage == null ? '' : coverage >= 1 ? 'ok' : coverage >= 0.5 ? 'warn' : 'bad');

/** The flag, in words ("company rate: only 3 closed quotations"). */
export function rateNote(n: Pick<PickedRate, 'source' | 'flag' | 'history'>): string | null {
  const h = n.history;
  const why = n.flag === 'zero' ? `no win from ${h.closed} closed quotation${h.closed === 1 ? '' : 's'}` : n.flag === 'thin' ? (h.closed ? `only ${h.closed} closed quotation${h.closed === 1 ? '' : 's'} in 12 months` : 'no closed quotation in 12 months') : null;
  if (n.source === 'own') return null;
  if (n.source === 'company') return `company rate (${why})`;
  return `not enough history to work it out (${why})`;
}
